<div class="justup-dialog">
  <vl-header-dialog (close)="dialogRef.close()" [rightElement]="excelBtn">
    {{ 'dashboard_ui.w_nc_stats' | trans }}
  </vl-header-dialog>

  <ng-template #excelBtn>
    <vl-excel-export-button size="medium"
                            [loading]="exporting()"
                            [disabled]="exporting()"
                            [tooltip]="'dashboard_ui.export_excel' | trans"
                            (action)="exportExcel()"/>
  </ng-template>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- Stats cards top row -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="secondary" icon="warning" title="Total NC" style="flex: 1; min-width: 150px">
        <span content class="headline">{{ data.data?.analytics?.totalCount ?? 0 }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="warn" icon="error" title="Critiques ouvertes" style="flex: 1; min-width: 150px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ data.data?.stats?.critiquesOuvertes ?? 0 }}</span>
          <span class="tiny" style="opacity: 0.7">/ {{ data.data?.stats?.ouvertes ?? 0 }} ouvertes</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="valid" icon="euro" title="Coût total estimé" style="flex: 1; min-width: 150px">
        <span content class="headline">{{ (data.data?.analytics?.coutTotal ?? 0) | number:'1.0-0' }} €</span>
      </vl-radio-card>

      <vl-radio-card mode="display"
                     color="secondary"
                     icon="trending_up"
                     title="Taux d'échappées"
                     [matTooltip]="'dashboard_ui.nc_kpi_taux_echappees_hint' | trans"
                     style="flex: 1; min-width: 150px">
        <span content class="headline">
          {{ data.data?.stats?.tauxEchappees == null ? '—' : (data.data?.stats?.tauxEchappees + '%') }}
        </span>
      </vl-radio-card>
    </div>

    <!-- Origine — distribution % (bars utiles ici) -->
    @if (origineRows().length) {
      <div class="section">
        <span class="subheading-2">{{ 'dashboard_ui.nc_titre_origine' | trans }}</span>
        <div class="flex-col gap-4 mt-8">
          @for (o of origineRows(); track o.key) {
            <div class="flex-row align-center gap-8">
              <span [style.background-color]="o.color" class="border-full shrink-0" style="width: 10px; height: 10px"></span>
              <span class="caption shrink-0" style="min-width: 120px">{{ o.label }}</span>
              <div class="grow bar-track">
                <div class="bar-fill" [style.width]="o.percentage + '%'" [style.background-color]="o.color"></div>
              </div>
              <span class="tiny on-disabled shrink-0" style="min-width: 90px; text-align: right">{{ o.count }} ({{ o.percentage }}%)</span>
            </div>
          }
        </div>
      </div>
    }

    <!-- Tableaux : Par type + Par gravité (côte à côte) -->
    <div class="grid-2 gap-12">
      <div class="section">
        <span class="subheading-2">Par type</span>
        <table class="data-table mt-8">
          <thead>
            <tr>
              <th>Type</th>
              <th class="num">Nb NC</th>
              <th class="num">Coût (€)</th>
            </tr>
          </thead>
          <tbody>
            @for (b of byType(); track b.key) {
              <tr>
                <td>{{ b.label }}</td>
                <td class="num">{{ b.count }}</td>
                <td class="num">{{ b.cout | number:'1.0-0' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="3" class="empty">—</td></tr>
            }
          </tbody>
        </table>
      </div>

      <div class="section">
        <span class="subheading-2">Par gravité</span>
        <table class="data-table mt-8">
          <thead>
            <tr>
              <th>Gravité</th>
              <th class="num">Nb NC</th>
              <th class="num">Coût (€)</th>
            </tr>
          </thead>
          <tbody>
            @for (b of byGravite(); track b.key) {
              <tr>
                <td><span class="dot" [style.background-color]="graviteColor(b.key)"></span> {{ b.label }}</td>
                <td class="num">{{ b.count }}</td>
                <td class="num">{{ b.cout | number:'1.0-0' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="3" class="empty">—</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <!-- Évolution mensuelle (timeline = bars utiles) -->
    <div class="section">
      <span class="subheading-2">{{ 'dashboard_ui.nc_titre_evolution' | trans }}</span>
      <div class="flex-col gap-4 mt-8">
        @for (m of byMonth(); track m.month) {
          <div class="flex-row align-center gap-8">
            <span class="caption shrink-0" style="min-width: 80px">{{ m.month }}</span>
            <div class="grow bar-track">
              <div class="bar-fill" [style.width]="monthBarWidth(m.count)" style="background: #36A2EB"></div>
            </div>
            <span class="tiny on-disabled shrink-0" style="min-width: 60px; text-align: right">{{ m.count }} NC</span>
            <span class="tiny on-disabled shrink-0" style="min-width: 80px; text-align: right">{{ m.cout | number:'1.0-0' }} €</span>
          </div>
        } @empty {
          <span class="caption on-disabled">—</span>
        }
      </div>
    </div>

    <!-- Tableaux : Top tiers + Top articles -->
    <div class="grid-2 gap-12">
      <div class="section">
        <span class="subheading-2">{{ 'dashboard_ui.nc_titre_top_tiers' | trans }}</span>
        <table class="data-table mt-8">
          <thead>
            <tr>
              <th>Tiers</th>
              <th class="num">Nb NC</th>
              <th class="num">Coût (€)</th>
            </tr>
          </thead>
          <tbody>
            @for (b of topTiers(); track b.key) {
              <tr>
                <td class="ellipsis-td">{{ b.label }}</td>
                <td class="num">{{ b.count }}</td>
                <td class="num">{{ b.cout | number:'1.0-0' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="3" class="empty">Aucun tiers lié</td></tr>
            }
          </tbody>
        </table>
      </div>

      <div class="section">
        <span class="subheading-2">{{ 'dashboard_ui.nc_titre_top_articles' | trans }}</span>
        <table class="data-table mt-8">
          <thead>
            <tr>
              <th>Article</th>
              <th class="num">Nb NC</th>
              <th class="num">Coût (€)</th>
            </tr>
          </thead>
          <tbody>
            @for (b of topArticles(); track b.key) {
              <tr>
                <td class="ellipsis-td">{{ b.label }}</td>
                <td class="num">{{ b.count }}</td>
                <td class="num">{{ b.cout | number:'1.0-0' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="3" class="empty">Aucun article lié</td></tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <!-- NC critiques ouvertes -->
    @if (criticalOpen().length) {
      <div class="section">
        <span class="subheading-2">{{ 'dashboard_ui.nc_titre_critiques' | trans }}</span>
        <div class="flex-col gap-6 mt-8">
          @for (nc of criticalOpen(); track nc.id) {
            <div class="critical-row">
              <div class="flex-row align-center gap-8">
                <span class="subheading-2 error">{{ nc.numero }}</span>
                <span class="caption on-background-alt">{{ nc.type }}</span>
                @if (nc.tiersLabel) {
                  <span class="caption on-background-alt">· {{ nc.tiersLabel }}</span>
                }
                <span class="ml-auto caption on-background-alt">{{ nc.dateOuverture | date:'dd/MM/yyyy' }}</span>
                @if (nc.coutEstime != null) {
                  <span class="caption on-disabled">· {{ nc.coutEstime | number:'1.0-0' }} €</span>
                }
              </div>
              <div class="caption on-background mt-2 desc">{{ nc.description }}</div>
            </div>
          }
        </div>
      </div>
    }
  </div>
</div>

<style>
.justup-dialog .section {
  background: var(--color-canvas);
  border: 1px solid var(--color-on-disabled);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
}

.justup-dialog .grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 720px) {
  .justup-dialog .grid-2 {
    grid-template-columns: 1fr;
  }
}

/* Bars (utilisés pour Origine % et Évolution mensuelle uniquement). */
.justup-dialog .bar-track {
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  overflow: hidden;
  min-width: 60px;
}

.justup-dialog .bar-fill {
  height: 100%;
  border-radius: 4px;
  background: #36A2EB;
  transition: width 0.2s ease;
}

/* Tables */
.justup-dialog .data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.justup-dialog .data-table th,
.justup-dialog .data-table td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--color-on-disabled);
  text-align: left;
}

.justup-dialog .data-table thead th {
  color: var(--color-on-background-alt);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid var(--color-on-disabled);
}

.justup-dialog .data-table td.num,
.justup-dialog .data-table th.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  width: 80px;
}

.justup-dialog .data-table td.empty {
  color: var(--color-on-disabled);
  text-align: center;
  font-style: italic;
}

.justup-dialog .data-table .ellipsis-td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.justup-dialog .data-table .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

/* NC critiques */
.justup-dialog .critical-row {
  background: var(--color-canvas);
  border: 1px solid var(--color-on-disabled);
  border-left: 3px solid var(--color-error);
  border-radius: 8px;
  padding: 8px 12px;
}

.justup-dialog .critical-row .desc {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
